import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  Card,
  CardContent,
  Stack,
  Chip,
} from "@mui/material";
import {
  CalendarToday,
  Email,
  CheckCircle,
  LinkOff,
} from "@mui/icons-material";
import ActionsService from "../../../services/actions.service";
import GoogleCalendarDialog from "../../integration/GoogleCalendarDialog";

const useQuery = () => new URLSearchParams(useLocation().search);

const SimpleSchedulingStep = forwardRef(({ formData, updateFormData }, ref) => {
  const query = useQuery();
  const connectedFromUrl = query.get("connected") === "true";

  const [enableScheduling, setEnableScheduling] = useState(false);
  const [activeConnection, setActiveConnection] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Expose validation to parent
  useImperativeHandle(ref, () => ({
    validateStep: () => {
      if (enableScheduling && !activeConnection) {
        setError("Please connect your Google Calendar before proceeding.");
        return false;
      }
      return true;
    },
  }));

  // Initialize enableScheduling only once
  useEffect(() => {
    if (connectedFromUrl) {
      setEnableScheduling(true);
    } else if (formData.scheduleEnabled) {
      setEnableScheduling(formData.scheduleEnabled);
    }
  }, [connectedFromUrl, formData.scheduleEnabled]);

  // Sync with parent formData only when changed
  useEffect(() => {
    const newData = {
      scheduleEnabled: enableScheduling,
      scheduleConnected: !!activeConnection,
    };

    if (
      formData.scheduleEnabled !== newData.scheduleEnabled ||
      formData.scheduleConnected !== newData.scheduleConnected
    ) {
      updateFormData(newData);
    }
  }, [
    enableScheduling,
    activeConnection,
    updateFormData,
    formData.scheduleEnabled,
    formData.scheduleConnected,
  ]);

  // Fetch existing connection if scheduling enabled
  useEffect(() => {
    const fetchConnection = async () => {
      if (!formData.id) return;
      try {
        const conn = await ActionsService.getConnection(formData.id);
        if (conn?.id) {
          setActiveConnection(conn);
          setEnableScheduling(true);
        }
      } catch (err) {
        console.error("Error fetching connection:", err);
      }
    };
    fetchConnection();
  }, [formData.id]);

  // Fetch events only if connected
  useEffect(() => {
    const fetchEvents = async () => {
      if (!activeConnection) return;
      try {
        const evts = await ActionsService.listEvents({});
        const normalized = (evts || []).map((evt) => ({
          id: evt.id || Date.now().toString(),
          title: evt.title || evt.summary || "Untitled",
          start: evt.start?.dateTime || evt.start?.date || evt.start,
          end: evt.end?.dateTime || evt.end?.date || evt.end,
        }));
        setEvents(normalized);
      } catch (err) {
        console.error("Error fetching events:", err);
      }
    };
    fetchEvents();
  }, [activeConnection]);

  const handleConnectCalendar = async () => {
    setError(null);
    try {
      const { url } = await ActionsService.getAuthUrl(formData.id, "agent");
      window.location.href = url;
    } catch (err) {
      setError(err.message || "Failed to connect Google Calendar");
    }
  };

  const handleDisconnect = async () => {
    try {
      await ActionsService.disconnect();
      setActiveConnection(null);
      setEvents([]);
      setEnableScheduling(false);

      const url = new URL(window.location.href);
      url.searchParams.delete("connected");
      window.history.replaceState({}, document.title, url.toString());
    } catch (err) {
      console.error("❌ Disconnect error:", err);
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 800,
        mx: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Box sx={{ textAlign: "center" }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Capture More Leads 📅
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Optional: Let customers book meetings when they're ready to talk
        </Typography>
      </Box>

      <Alert severity="info">
        📧 <strong>You'll always capture emails</strong> – Every lead goes to
        your dashboard for follow-up
      </Alert>

      {error && <Alert severity="error">{error}</Alert>}

      <Card sx={{ border: "1px solid rgba(0,0,0,0.08)" }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Email sx={{ fontSize: 28, color: "primary.main", mr: 2 }} />
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Email Capture
              </Typography>
              <Typography variant="body2" color="text.secondary">
                When AI can't answer, it captures customer email for you to
                follow up
              </Typography>
            </Box>
          </Box>

          <Box sx={{ borderTop: "1px solid rgba(0,0,0,0.08)", pt: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={enableScheduling}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    if (!checked) handleDisconnect();
                    setEnableScheduling(checked);
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Enable Scheduling
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Let customers book time to speak with you
                  </Typography>
                </Box>
              }
              sx={{ width: "100%" }}
            />
          </Box>

          {enableScheduling && (
            <Box sx={{ mt: 3, textAlign: "center" }}>
              {activeConnection ? (
                <Stack spacing={2} alignItems="center">
                  <Chip
                    icon={<CheckCircle />}
                    label="Connected Google Calendar"
                    color="success"
                    variant="filled"
                    sx={{ fontWeight: 600 }}
                  />
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<LinkOff />}
                    onClick={handleDisconnect}
                  >
                    Disconnect
                  </Button>
                  {/* <Button
                    variant="contained"
                    onClick={() => setDialogOpen(true)}
                    sx={{ mt: 1 }}
                  >
                    View Events
                  </Button> */}
                </Stack>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<CalendarToday />}
                  onClick={handleConnectCalendar}
                  fullWidth
                >
                  Connect Google Calendar
                </Button>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <GoogleCalendarDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        events={events}
        setEvents={setEvents}
        onDisconnect={handleDisconnect}
      />
    </Box>
  );
});

export default SimpleSchedulingStep;
